/**
 * Serialize data using provided handler functions.
 * It is possible to serialize arrays by recursive calling this function inside handlers.
 *
 * Example:
 * ```
 * interface TestInput {
 *   a: string;
 *   b: number;
 *   optionalSkip: string;
 * }
 *
 * interface TestOutput {
 *   a: number;
 *   c: boolean;
 *   isOptionalSkipWritten?: boolean;
 * }
 *
 * const input: TestInput = {
 *   a: '1',
 *   b: 2,
 *   optionalSkip: 'skip',
 * };
 *
 * const output: TestOutput = unifiedSerializer<TestInput, TestOutput>(input, {
 *   a: {
 *     propertyToWrite: 'a',
 *     serializeMethod: (dataToTransform: string): number => +dataToTransform,
 *   },
 *   b: {
 *     propertyToWrite: 'c',
 *     serializeMethod: (dataToTransform: number): boolean => !!dataToTransform,
 *   },
 *   optionalSkip: {
 *     propertyToWrite: 'isOptionalSkipWritten',
 *     serializeMethod: (): boolean => true,
 *
 *     // Changing to any other string will result in the property being written.
 *     // Can be useful when you need to write a property conditionally
 *     // (for example, when backend doesn't want to have the property at all if it is falsy).
 *     shouldBeWritten: (dataToTransform: string): boolean => dataToTransform !== 'skip',
 *   }
 * });
 *
 * console.log(output);
 * ```
 */
export const unifiedSerializer = <ObjectToTransform, ResultObject>(
  dataToTransform: ObjectToTransform,
  handlerDict: DataDescriptor<ObjectToTransform, ResultObject>,
): ResultObject => {
  // Create an empty object to store a result.
  const result: Partial<ResultObject> = {};

  // Iterate over all keys in the provided handler dictionary.
  for (const key in handlerDict) {

    // Check if the handler key is present in the provided data.
    if (Object.prototype.hasOwnProperty.call(dataToTransform, key)) {
      // Extract provided configs for the matched property.
      const propertyMetadata = handlerDict[key] as SerializerPropertyMetadata<ObjectToTransform[keyof ObjectToTransform], ResultObject>;

      // Define data to transform.
      const propertyData: ObjectToTransform[keyof ObjectToTransform] = dataToTransform[key];

      // Extract checker function for the matched property.
      const shouldBeWritten: ShouldPropertyBeWrittenHandler<ObjectToTransform[keyof ObjectToTransform]> = propertyMetadata.shouldBeWritten;

      // Do not write the current property if checker function is provided and the result of its calling is falsy.
      if (shouldBeWritten && !shouldBeWritten(propertyData)) {
        continue;
      }

      // Extract handler function for the matched property.
      const propertyHandler: SerializationPropertyHandler<ObjectToTransform[keyof ObjectToTransform], ResultObject> =
        propertyMetadata.serializeMethod;

      // Extract an expected output key for the matched property.
      const outputKey: keyof ResultObject = propertyMetadata.propertyToWrite;

      // Check if the output key already exists in the result object.
      if (result[outputKey] !== undefined) {
        console.warn(`Property with key '${String(outputKey)}' already exists in the result object. It will be overwritten.`);
      }

      // Serialization: apply handler function to the matched property.
      result[outputKey] = propertyHandler(propertyData);

    }
  }

  // Return result.
  return result as ResultObject;
};

/**
 * Defines relation structure for serialization configs.
 * Supposed to have the property key as `key` and the serialization config for the property value as `value`.
 */
export type DataDescriptor<Input, Output> = {
  [key in keyof Partial<Input>]: SerializerPropertyMetadata<Input[key], Output>;
};

/**
 * Required config fields for applying serialization.
 */
export interface SerializerPropertyMetadata<Input, Output> {
  /** Defines where to write the serialized data in the result object. */
  propertyToWrite: keyof Output;

  /** Contain serialization function. */
  serializeMethod: SerializationPropertyHandler<Input, Output>;

  /** Contain function which defines if the data should be included. */
  shouldBeWritten?: ShouldPropertyBeWrittenHandler<Input>;
}

/**
 * Serialization function.
 * Will be applied to the matched property.
 */
export type SerializationPropertyHandler<Input, Output> = (dataToTransform: Input) => Output[keyof Output];

/**
 * Defines whether the provided property should be written to the result object.
 */
export type ShouldPropertyBeWrittenHandler<Input> = (dataToTransform: Input) => boolean;
